import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { 
  fadeInScale, 
  terminalAnimations, 
  terminalTransitions,
  animationConfig,
  cssAnimations,
  animationProps
} from '../styles/animations';
// Performance hooks removed as they're not currently implemented in the UI

const DemoSection = styled.section`
  padding: 4rem 1.5rem;
  background: linear-gradient(
    180deg,
    rgba(245, 245, 247, 0) 0%,
    ${props => props.theme.colors.surfaceLight} 9%,
    ${props => props.theme.colors.surfaceLight} 91%,
    rgba(245, 245, 247, 0) 100%
  );

  @media (max-width: 700px) {
    padding: 3rem 1rem;
  }
`;

const DemoInner = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const SectionKicker = styled.p`
  margin-bottom: 0.35rem;
  color: ${props => props.theme.colors.textTertiary};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.11em;
`;

const AttackTypeSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AttackGroup = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.72rem;
  border: 1px solid rgba(0, 0, 0, 0.045);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.48);
  ${props => props.fullWidth && `
    grid-column: 1 / -1;
  `}
`;

const GroupTitle = styled.h3`
  padding: 0 0.2rem;
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.63rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const GroupButtons = styled.div<{ centered?: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.4rem;
  ${props => props.centered && `
    grid-template-columns: minmax(220px, 360px);
    justify-content: center;
  `}

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const AttackTypeButton = styled.button<{ active: boolean }>`
  min-height: 2.9rem;
  padding: 0.55rem 0.65rem;
  border-radius: 0.72rem;
  border: 1px solid ${props => props.active ? 'rgba(0, 113, 227, 0.28)' : 'rgba(0, 0, 0, 0.045)'};
  background: ${props => props.active 
    ? 'rgba(0, 113, 227, 0.055)'
    : 'rgba(255, 255, 255, 0.72)'};
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  cursor: pointer;
  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
  will-change: transform, box-shadow, background;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.42rem;
  width: 100%;
  position: relative;
  overflow: hidden;
  text-align: left;
  box-shadow: ${props => props.active ? '0 3px 12px rgba(0, 113, 227, 0.07)' : 'none'};

  &:hover {
    border-color: rgba(0, 113, 227, 0.22);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.045);
    background: ${props => props.active ? 'rgba(0, 113, 227, 0.07)' : 'rgba(255, 255, 255, 0.96)'};
  }

  /* Removed shimmer effect for better performance */

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .attack-dot {
    width: 0.42rem;
    height: 0.42rem;
    min-width: 0.42rem;
    border-radius: 50%;
    box-shadow: 0 0 0 3px color-mix(in srgb, currentColor 10%, transparent);
  }

  .attack-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    text-align: left;
    flex: 1;
  }

  .attack-name {
    font-size: 0.74rem;
    font-weight: 650;
    line-height: 1.28;
    text-align: left;
    width: 100%;
  }

  .attack-description {
    display: none;
  }
`;

const DemoContainer = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
  will-change: transform;  /* Optimize transforms */
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.08);
  transition: box-shadow ${props => props.theme.transitions.fast};
  
  &:hover {
    box-shadow: 0 22px 58px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: ${props => props.theme.borderRadius.md};
  }
`;

const DemoMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  @media (max-width: 1024px) and (min-width: 769px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.8rem;
  background: rgba(0, 0, 0, 0.018);
  border-radius: 0.78rem;
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

const MetaLabel = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const MetaValue = styled.div`
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.4;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 6px;
  background-color: ${props => props.theme.colors.surfaceLight};
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 2rem;
  border: 1px solid ${props => props.theme.colors.border};
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    width: ${props => props.progress}%;
    transition: width 0.3s ease;
  }
`;

const StepInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const StatusValue = styled.span`
  color: inherit;
  font-weight: 600;
`;

const ControlBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ControlButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 480px) {
    justify-content: center;
    width: 100%;
  }
`;

const ControlButton = styled(motion.button)`
  min-height: 32px;
  padding: 0.42rem 0.64rem;
  background: rgba(255, 255, 255, 0.56);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 0.62rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.7rem;
  font-weight: 620;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.92);
    color: ${props => props.theme.colors.textPrimary};
    border-color: rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.55rem;
    font-size: 0.66rem;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Terminal = styled.div`
  background-color: ${props => props.theme.colors.terminal.background};
  color: #d7dee8;
  border-radius: 1.15rem;
  padding: 2rem;
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.875rem;
  height: 420px;
  max-height: 70vh;
  overflow-y: auto;
  position: relative;
  will-change: scroll-position;  /* Optimize scrolling */
  transform: translateZ(0);       /* Force hardware acceleration */
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 12px 30px rgba(0, 0, 0, 0.12);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    height: 400px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    height: 350px;
  }
`;

const TerminalContent = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  width: 100%;
`;

const RoundContainer = styled(motion.div)`
  margin-bottom: 1.5rem;
`;

const RoundHeader = styled(motion.div)`
  color: #60a5fa;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  text-align: center;
`;

const ThoughtBlock = styled(motion.div)`
  background-color: rgba(96, 165, 250, 0.1);
  border-left: 3px solid #60a5fa;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border-radius: 0 ${props => props.theme.borderRadius.sm} ${props => props.theme.borderRadius.sm} 0;
`;

const ActionBlock = styled(motion.div)`
  background-color: rgba(167, 139, 250, 0.1);
  border-left: 3px solid #a78bfa;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border-radius: 0 ${props => props.theme.borderRadius.sm} ${props => props.theme.borderRadius.sm} 0;
`;

const OutputBlock = styled(motion.div)`
  background-color: rgba(34, 197, 94, 0.1);
  border-left: 3px solid #22c55e;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border-radius: 0 ${props => props.theme.borderRadius.sm} ${props => props.theme.borderRadius.sm} 0;
  max-height: 200px;
  overflow-y: auto;
`;


const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #aeb8c2;
  font-size: 1rem;
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
    border: 2px solid ${props => props.theme.colors.border};
    border-top: 2px solid ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: ${animationProps.loadingSpin};
  }
  
  ${cssAnimations.spin}
`;

const AttackerRequest = styled(motion.div)`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
  border: 2px solid #ef4444;
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '⚠️ ATTACKER REQUEST';
    position: absolute;
    top: 0.5rem;
    left: 1rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #ef4444;
    letter-spacing: 0.1em;
  }
`;

const RequestContent = styled.div`
  margin-top: 1.5rem;
  font-size: 1rem;
  line-height: 1.6;
  color: #e7c16f;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
`;

interface Round {
  number: number;
  thought: string;
  action: string;
  content: string;
  output: string;
}

interface DemoData {
  user_request: string;
  attempt: number;
  result: boolean;
  reason: string;
  cua_framework: string;
  model: string;
  token_usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  history: string;
  total_attempts?: number;
  successful_attempt?: number;
  total_token_usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  attack_type?: string;
  description?: string;
  JSR?: boolean;
}

interface AttackType {
  id: string;
  name: string;
  description: string;
  color: string;
  dataFile: string;
}

interface AttackTypeGroup {
  title: string;
  types: AttackType[];
}

const attackTypeGroups: AttackTypeGroup[] = [
  {
    title: "End-to-End Kill Chain",
    types: [
      {
        id: 'kill_chain_c2',
        name: 'Build C2 Channel',
        description: 'A multi-stage trajectory from a misconfigured sudo rule to privilege escalation, lateral movement, and a command-and-control channel.',
        color: '#0071e3',
        dataFile: `${process.env.PUBLIC_URL || ''}/demo.json`
      },
      {
        id: 'kill_chain_advanced',
        name: 'Port Knocking',
        description: 'A SUID-based privilege-escalation path followed by lateral movement and a condition-triggered port-knocking mechanism.',
        color: '#5e5ce6',
        dataFile: `${process.env.PUBLIC_URL || ''}/demo.json`
      },
      {
        id: 'kill_chain_credential',
        name: 'Credentials Cracking',
        description: 'A chain that escalates privileges, accesses synthetic password hashes, and performs offline recovery in the sandbox.',
        color: '#34c759',
        dataFile: `${process.env.PUBLIC_URL || ''}/demo.json`
      }
    ]
  },
  {
    title: "TTP (Tactics, Techniques, and Procedures)",
    types: [
      {
        id: 'traffic_hijacking',
        name: 'Traffic Hijacking',
        description: 'A controlled man-in-the-middle scenario that captures synthetic HTTP traffic between sandbox hosts.',
        color: '#ff9f0a',
        dataFile: `${process.env.PUBLIC_URL || ''}/demo.json`
      },
      {
        id: 'installer_packages',
        name: 'Installer Packages',
        description: 'A crafted Debian package uses an installation hook to realize and verify a synthetic OS-level effect.',
        color: '#64d2ff',
        dataFile: `${process.env.PUBLIC_URL || ''}/demo.json`
      },
      {
        id: 'udev_rules',
        name: 'Udev Rules',
        description: 'A device-event-triggered persistence mechanism implemented through a udev rule in the isolated host.',
        color: '#ff375f',
        dataFile: `${process.env.PUBLIC_URL || ''}/demo.json`
      }
    ]
  },
  {
    title: "Additional Demonstration",
    types: [
      {
        id: 'apparent_cve_exploit',
        name: 'Privilege Escalation by CVE and Backdoor Installation',
        description: 'A recorded case in which an agent reasons about a vulnerable sudo version, drafts an exploit path, and proposes a multi-stage deployment workflow.',
        color: '#8e8e93',
        dataFile: `${process.env.PUBLIC_URL || ''}/demo.json`
      }
    ]
  }
];

// Flatten for backward compatibility
const attackTypes: AttackType[] = attackTypeGroups.flatMap(group => group.types);

const parseHistory = (history: string): Round[] => {
  const rounds: Round[] = [];
  // Split by Round headers and process each section
  const sections = history.split(/(?==== Round \d+ ===)/);
  
  // Helper function to process escape sequences
  const processEscapeSequences = (text: string): string => {
    return text
      .replace(/\\r\\n/g, '\n')  // Replace \r\n with newline
      .replace(/\\n/g, '\n')     // Replace \n with newline
      .replace(/\\r/g, '\r')     // Replace \r with carriage return
      .replace(/\\t/g, '\t')     // Replace \t with tab
      .replace(/\\\\/g, '\\')    // Replace \\ with single backslash
      .replace(/\\u001b\[[0-9;?]*[a-zA-Z]/g, '') // Remove ANSI escape codes
      .replace(/\\u001b\][0-9;]*;[^\\]*\\u0007/g, '') // Remove OSC sequences
      .replace(/\\u001b[>=]/g, '') // Remove other escape sequences
      .replace(/\\u0007/g, ''); // Remove bell character
  };
  
  sections.forEach(section => {
    const roundMatch = section.match(/=== Round (\d+) ===/);
    if (!roundMatch) return;
    
    const roundNumber = parseInt(roundMatch[1]);
    const roundContent = section;
    
    const thought = roundContent.match(/🧠 Agent thought: ([\s\S]*?)(?=\n⚡|$)/)?.[1]?.trim() || '';
    const action = roundContent.match(/⚡ Agent action: ([\s\S]*?)(?=\n📝|\n💻|$)/)?.[1]?.trim() || '';
    const contentRaw = roundContent.match(/📝 Agent content: ([\s\S]*?)(?=\n💻|$)/)?.[1]?.trim() || '';
    const outputRaw = roundContent.match(/💻 Environment output: ([\s\S]*?)(?=\n\n=== Round|$)/)?.[1]?.trim() || '';
    
    // Process escape sequences in both content and output
    const content = processEscapeSequences(contentRaw);
    const output = processEscapeSequences(outputRaw);
    
    rounds.push({
      number: roundNumber,
      thought,
      action,
      content,
      output
    });
  });
  
  return rounds;
};

export const Demo: React.FC = () => {
  const [currentRound, setCurrentRound] = useState(0);
  const [demoData, setDemoData] = useState<Round[]>([]);
  const [currentDemoInfo, setCurrentDemoInfo] = useState<DemoData | null>(null);
  const [showRequest, setShowRequest] = useState(true);
  const [selectedAttackType, setSelectedAttackType] = useState<string>('kill_chain_c2');
  const [isLoading, setIsLoading] = useState(false);
  const terminalRef = useRef<HTMLDivElement>(null);
  const demoSectionRef = useRef<HTMLDivElement>(null);
  
  // Performance optimization hooks removed for now

  const loadDemoData = async (attackTypeId: string) => {
    setIsLoading(true);
    const attackType = attackTypes.find(type => type.id === attackTypeId);
    if (!attackType) return;

    try {
      const response = await fetch(attackType.dataFile);
      const data = await response.json();
      
      // Handle both array and object formats
      let demoItem: DemoData;
      
      if (Array.isArray(data)) {
        // If it's an array, find the demo based on attack_type and specific criteria
        if (attackTypeId === 'kill_chain_c2') {
          // Look for the first End-to-End Kill Chain demo (C2 Channel version)
          demoItem = data.find(item => item.attack_type === 'End-to-End Kill Chain' && item.user_request && item.user_request.includes('c2_handler.sh')) || data[0];
        } else if (attackTypeId === 'kill_chain_advanced') {
          // Look for the advanced End-to-End Kill Chain demo (setuid version)
          demoItem = data.find(item => item.attack_type === 'End-to-End Kill Chain' && item.user_request && item.user_request.includes('port-knocking activated method')) || data[1];
        } else if (attackTypeId === 'kill_chain_credential') {
          // Look for the credential harvesting End-to-End Kill Chain demo (Gemini version)
          demoItem = data.find(item => item.attack_type === 'End-to-End Kill Chain' && item.cua_framework === 'Gemini CLI') || data.find(item => item.model === 'Gemini 2.5-Pro') || data[5];
        } else if (attackTypeId === 'traffic_hijacking') {
          // Look for Traffic Hijacking demo
          demoItem = data.find(item => item.attack_type === 'Traffic Hijacking') || data[2];
        } else if (attackTypeId === 'installer_packages') {
          // Look for Installer Packages demo
          demoItem = data.find(item => item.attack_type === 'Installer Packages') || data[3];
        } else if (attackTypeId === 'udev_rules') {
          // Look for Udev Rules demo
          demoItem = data.find(item => item.attack_type === 'Udev Rules') || data[4];
        } else if (attackTypeId === 'apparent_cve_exploit') {
          // Look for Apparent Demo
          demoItem = data.find(item => item.attack_type === 'Apparent Demo') || data[6];
        } else {
          // Default to first item
          demoItem = data[0];
        }
      } else {
        demoItem = data;
      }
      
      const history = demoItem.history;
      
      if (!history) {
        console.error('No history found in data');
        return;
      }
      
      // Store complete demo info
      setCurrentDemoInfo(demoItem);
      
      const parsedRounds = parseHistory(history);
      setDemoData(parsedRounds);
      
      // Reset demo state
      setCurrentRound(0);
      setShowRequest(true);
    } catch (error) {
      console.error('Error loading demo data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDemoData(selectedAttackType);
  }, [selectedAttackType]);

  useEffect(() => {
    // Auto-scroll terminal to top for new rounds
    if (terminalRef.current) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        if (terminalRef.current) {
          terminalRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [currentRound, showRequest]);

  const handlePrevious = () => {
    if (showRequest) {
      setShowRequest(false);
      return;
    }
    if (currentRound > 0) {
      setCurrentRound(prev => prev - 1);
    } else {
      setShowRequest(true);
    }
  };

  const handleNext = () => {
    if (showRequest) {
      setShowRequest(false);
      return;
    }
    if (currentRound < demoData.length - 1) {
      setCurrentRound(prev => prev + 1);
    }
  };

  const handleReset = () => {
    setCurrentRound(0);
    setShowRequest(true);
    
    // Auto scroll to demo section
    if (demoSectionRef.current) {
      demoSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const currentAttackType = attackTypes.find(type => type.id === selectedAttackType);

  return (
    <DemoSection id="demo" ref={demoSectionRef}>
      <DemoInner>
      <SectionKicker>Demo</SectionKicker>

      <AttackTypeSelector>
        {attackTypeGroups.map((group) => (
          <AttackGroup key={group.title} fullWidth={group.title === "Additional Demonstration"}>
            <GroupTitle>{group.title}</GroupTitle>
            <GroupButtons centered={group.title === "Additional Demonstration"}>
              {group.types.map((attackType) => (
                <AttackTypeButton
                  key={attackType.id}
                  active={selectedAttackType === attackType.id}
                  onClick={() => setSelectedAttackType(attackType.id)}
                  disabled={isLoading}
                >
                  <span className="attack-dot" style={{ backgroundColor: attackType.color, color: attackType.color }} aria-hidden="true" />
                  <div className="attack-info">
                    <div className="attack-name">{attackType.name}</div>
                    <div className="attack-description">
                      {attackType.description}
                    </div>
                  </div>
                </AttackTypeButton>
              ))}
            </GroupButtons>
          </AttackGroup>
        ))}
      </AttackTypeSelector>
      
      <DemoContainer>
        <DemoMeta>
          <MetaItem>
            <MetaLabel>CLI agent</MetaLabel>
            <MetaValue>{currentDemoInfo?.cua_framework || 'Loading...'}</MetaValue>
          </MetaItem>
          
          <MetaItem>
            <MetaLabel>Foundation model</MetaLabel>
            <MetaValue>{currentDemoInfo?.model || 'Loading...'}</MetaValue>
          </MetaItem>
          
          <MetaItem>
            <MetaLabel>Trajectory status</MetaLabel>
            <MetaValue>
              <StatusValue>
                {showRequest ? 'Request' : currentRound === demoData.length - 1 ? 'Completed' : `Round ${currentRound + 1}`}
              </StatusValue>
            </MetaValue>
          </MetaItem>
        </DemoMeta>
        
        <ProgressBar progress={demoData.length ? (currentRound + 1) / demoData.length * 100 : 0} />
        
        <Terminal ref={terminalRef}>
          <TerminalContent>
            <AnimatePresence mode="wait">
              {isLoading ? (
                <LoadingIndicator key="loading">
                  Loading {currentAttackType?.name} demo...
                </LoadingIndicator>
              ) : showRequest && currentDemoInfo?.user_request ? (
                <AttackerRequest
                  key="attacker-request"
                  variants={fadeInScale}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ 
                    duration: animationConfig.durations.normal,
                    ease: animationConfig.easing.smooth
                  }}
                >
                  <RequestContent>{currentDemoInfo.user_request}</RequestContent>
                </AttackerRequest>
              ) : demoData.length > 0 && !showRequest ? (
                <RoundContainer
                  key={`round-${currentRound}`}
                  variants={terminalAnimations.roundContainer}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={terminalTransitions.roundContainer}
                >
                  <RoundHeader
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    === Round {demoData[currentRound].number} ===
                  </RoundHeader>
                  
                  <ThoughtBlock
                    variants={terminalAnimations.thoughtBlock}
                    initial="initial"
                    animate="animate"
                    transition={terminalTransitions.thoughtBlock}
                  >
                    <strong>🧠 Agent thought:</strong> {demoData[currentRound].thought}
                  </ThoughtBlock>
                  
                  <ActionBlock
                    variants={terminalAnimations.actionBlock}
                    initial="initial"
                    animate="animate"
                    transition={terminalTransitions.actionBlock}
                  >
                    <strong>⚡ Agent action:</strong> {demoData[currentRound].action}
                    {demoData[currentRound].content && demoData[currentRound].content !== 'None' && (
                        <motion.div 
                        style={{ marginTop: '0.5rem' }}
                        variants={terminalAnimations.contentReveal}
                        initial="initial"
                        animate="animate"
                        transition={terminalTransitions.contentReveal}
                      >
                        <strong>📝 Content:</strong>
                        <SyntaxHighlighter 
                          language="bash" 
                          style={vscDarkPlus}
                          customStyle={{
                            margin: '0.5rem 0',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            borderRadius: '4px'
                          }}
                        >
                          {demoData[currentRound].content}
                        </SyntaxHighlighter>
                      </motion.div>
                    )}
                  </ActionBlock>
                  
                  <OutputBlock
                    variants={terminalAnimations.outputBlock}
                    initial="initial"
                    animate="animate"
                    transition={terminalTransitions.outputBlock}
                  >
                    <strong>💻 Environment output:</strong>
                    <motion.pre 
                      style={{ margin: '0.5rem 0', fontSize: '0.8rem' }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 2, duration: 0.5 }}
                    >
                      {demoData[currentRound].output || 'No output'}
                    </motion.pre>
                  </OutputBlock>
                </RoundContainer>
              ) : null}
            </AnimatePresence>
          </TerminalContent>
        </Terminal>
        
        <ControlBar>
          <StepInfo>
            Step {currentRound + 1} of {demoData.length}
          </StepInfo>
          
          <ControlButtons>
            <ControlButton 
              onClick={handlePrevious} 
              disabled={showRequest && currentRound === 0}
            >
              <span aria-hidden="true">←</span> Previous
            </ControlButton>
            <ControlButton 
              onClick={handleNext} 
              disabled={!showRequest && currentRound === demoData.length - 1}
            >
              Next <span aria-hidden="true">→</span>
            </ControlButton>
            <ControlButton 
              onClick={handleReset}
            >
              Reset
            </ControlButton>
          </ControlButtons>
        </ControlBar>
      </DemoContainer>
      </DemoInner>
    </DemoSection>
  );
};
